/**
 * Catálogo de falhas do bootstrap de autenticação/autorização.
 *
 * Toda falha que impeça o usuário de operar o painel (token, GetAuthUser,
 * GetUserMenu, GetUserPermissions, loop de redirect, timeout) deve terminar
 * aqui: log estruturado -> logout + limpeza -> toast -> /login com o motivo.
 */

export type AuthFailureReason =
  | 'session_expired'
  | 'token_refresh_failed'
  | 'user_fetch_failed'
  | 'menu_fetch_failed'
  | 'permissions_fetch_failed'
  | 'no_permissions'
  | 'inconsistent_permissions'
  | 'oauth_state_missing'
  | 'oauth_failed'
  | 'bootstrap_timeout'
  | 'redirect_loop'
  | 'unknown_error';

export interface AuthFailureCopy {
  /** Título curto (toast + tela de login) */
  title: string;
  /** Mensagem para o usuário final, em pt-BR */
  message: string;
}

/** Mensagens exibidas ao usuário por motivo de falha. */
export const AUTH_FAILURE_COPY: Record<AuthFailureReason, AuthFailureCopy> = {
  session_expired: {
    title: 'Sessão expirada',
    message: 'Sua sessão expirou. Faça login novamente para continuar.',
  },
  token_refresh_failed: {
    title: 'Sessão expirada',
    message: 'Não foi possível renovar sua sessão. Faça login novamente.',
  },
  user_fetch_failed: {
    title: 'Falha ao carregar seu usuário',
    message:
      'Não foi possível carregar os dados do seu usuário (GetAuthUser). Faça login novamente ou contate o administrador.',
  },
  menu_fetch_failed: {
    title: 'Falha ao carregar o menu',
    message:
      'Não foi possível carregar os módulos disponíveis (GetUserMenu). Faça login novamente ou contate o administrador.',
  },
  permissions_fetch_failed: {
    title: 'Falha ao carregar permissões',
    message:
      'Não foi possível carregar suas permissões (GetUserPermissions). Faça login novamente ou contate o administrador.',
  },
  no_permissions: {
    title: 'Sem permissões de acesso',
    message:
      'Você não possui permissões para acessar o sistema. Entre em contato com o administrador.',
  },
  inconsistent_permissions: {
    title: 'Permissões inconsistentes',
    message:
      'Suas permissões não permitem abrir nenhuma página do painel. Entre em contato com o administrador.',
  },
  oauth_state_missing: {
    title: 'Falha no login social',
    message: 'O retorno do login não pôde ser validado (state ausente). Tente entrar novamente.',
  },
  oauth_failed: {
    title: 'Falha no login social',
    message: 'Não foi possível concluir a autenticação. Tente entrar novamente.',
  },
  bootstrap_timeout: {
    title: 'Tempo esgotado',
    message:
      'O servidor demorou demais para responder durante a autenticação. Tente entrar novamente.',
  },
  redirect_loop: {
    title: 'Loop de redirecionamento',
    message:
      'Detectamos um redirecionamento em loop causado por permissões inconsistentes. Você foi desconectado por segurança.',
  },
  unknown_error: {
    title: 'Erro inesperado na autenticação',
    message: 'Ocorreu um erro inesperado ao autenticar. Faça login novamente.',
  },
};

/** Detalhe persistido para a tela de login exibir o motivo após o redirect. */
export interface AuthFailureDetail {
  reason: AuthFailureReason;
  title: string;
  message: string;
  /** Mensagem crua do backend / exceção, para diagnóstico em tela. */
  technicalMessage?: string;
  /** Código gRPC/HTTP quando disponível. */
  code?: number | string;
  /** URL em que a falha ocorreu. */
  url?: string;
  /** ISO timestamp da falha. */
  at: string;
}

export const AUTH_FAILURE_STORAGE_KEY = 'auth_failure';

/** Contador de reloads pós-refresh, usado para cortar loop de recarregamento. */
export const AUTH_RELOAD_ATTEMPTS_KEY = 'auth_reload_attempts';

/** Timeout máximo de cada chamada do bootstrap de autenticação. */
export const AUTH_BOOTSTRAP_TIMEOUT_MS = 20_000;

/**
 * Erro de controle de fluxo do bootstrap: carrega o motivo até o `catchError`
 * único do guard, garantindo um só ponto de saída (sem logout duplicado).
 */
export class AuthBootstrapError extends Error {
  constructor(
    readonly reason: AuthFailureReason,
    readonly cause?: unknown,
    readonly context?: Record<string, unknown>
  ) {
    super(`[AuthBootstrap] ${reason}`);
    this.name = 'AuthBootstrapError';
  }
}
